import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Clock, Loader2, Target, Bookmark, Lock, Trophy } from 'lucide-react';
import { recentStore } from '../lib/storage';
import { subjectMeta } from '../lib/section-meta';
import { accent, springs } from '../lib/utils-app';
import api from '../lib/api';
import { SOLVEIT_EXAMS } from '../lib/solveit';

export default function Home() {
  const [subjects, setSubjects] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const recents = recentStore.list();

  useEffect(() => {
    let alive = true;
    Promise.all([api.listSubjects(), api.stats()])
      .then(([subs, st]) => {
        if (!alive) return;
        setSubjects(subs);
        setStats(st);
      })
      .catch((err) => alive && setError(err.message || 'Failed to load'));
    return () => { alive = false; };
  }, []);

  return (
    <div>
      <HeroBanner stats={stats} />
      {recents.length > 0 && <ContinueStrip items={recents.slice(0, 3)} />}
      <SubjectsGrid subjects={subjects} error={error} />
      <HowItWorksStrip />
      <OtherExamsStrip />
    </div>
  );
}

function HeroBanner({ stats }) {
  return (
    <section className="relative mb-14">
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.soft}
        className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-5"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
        JEE Advanced · PYQ Companion
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.slow, delay: 0.05 }}
        className="font-serif-q text-[44px] md:text-[68px] leading-[1.02] tracking-[-0.022em] text-slate-50 max-w-3xl"
      >
        Your <em className="italic text-indigo-300 font-normal">Companion</em> for <span className="italic">JEE Advanced</span>
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springs.slow, delay: 0.12 }}
        className="mt-5 font-serif-q text-[18px] md:text-[22px] leading-[1.45] text-slate-400 max-w-2xl tracking-[-0.005em]"
      >
        Practice every PYQ. <span className="italic text-amber-300 font-medium">Master every pattern.</span>
      </motion.p>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22, duration: 0.4 }}
        className="mt-8 flex flex-wrap items-end gap-8"
      >
        <Stat label="Maths questions" value={stats?.questions ? stats.questions.toLocaleString() : '—'} />
        <Stat label="Years covered" value={stats?.minYear ? `${stats.minYear} – ${stats.maxYear}` : '—'} />
        <AspirantBadge />
      </motion.div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="font-serif-q text-2xl text-slate-100 tracking-tight whitespace-nowrap">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function AspirantBadge() {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={springs.snappy}
      className="relative inline-flex items-center gap-2.5 pl-2 pr-3.5 h-[44px] rounded-full bg-gradient-to-r from-amber-400/15 via-rose-400/10 to-indigo-400/15 ring-1 ring-amber-400/25 backdrop-blur-sm overflow-hidden"
    >
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(251,191,36,0.18),transparent_60%)] pointer-events-none" />
      <span className="relative w-7 h-7 rounded-full bg-amber-400/20 ring-1 ring-amber-400/40 flex items-center justify-center text-amber-300">
        <Trophy className="w-3.5 h-3.5" strokeWidth={2.2} fill="currentColor" fillOpacity={0.15} />
      </span>
      <span className="relative leading-tight">
        <span className="block font-serif-q text-[14.5px] text-slate-100 tracking-tight italic">Built for AIR</span>
        <span className="block text-[9.5px] uppercase tracking-[0.18em] text-amber-300 font-semibold">aspirant mode</span>
      </span>
    </motion.div>
  );
}

function ContinueStrip({ items }) {
  return (
    <section className="mb-12">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-3">
        <Clock className="w-3.5 h-3.5" /> Continue
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {items.map((r) => (
          <Link key={`${r.subjectId || 'math'}-${r.sectionId}-${r.topicId}`}
            to={`/subject/${r.subjectId || 'math'}/section/${r.sectionId}/topic/${r.topicId}`}
            className="group rounded-xl glass hairline px-4 py-3.5 hover:bg-white/[0.04] transition">
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500">{r.sectionName}</div>
            <div className="font-serif-q text-[17px] text-slate-100 mt-0.5 group-hover:text-indigo-200">{r.topicName}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function SubjectsGrid({ subjects, error }) {
  if (error) {
    return <div className="rounded-2xl glass hairline p-8 text-center text-rose-300">Failed to load: {error}</div>;
  }
  if (!subjects) {
    return (
      <div className="rounded-2xl glass hairline p-12 flex items-center justify-center text-slate-400 gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading subjects…
      </div>
    );
  }
  return (
    <section>
      <div className="flex items-end justify-between mb-5">
        <h2 className="font-serif-q text-2xl tracking-tight text-slate-100">Pick a subject</h2>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{subjects.filter((s) => s.status === 'live').length} live</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {subjects.map((s, i) => <SubjectCard key={s.id} subject={s} delay={i * 0.04} />)}
      </div>
    </section>
  );
}

function SubjectCard({ subject, delay }) {
  const meta = subjectMeta(subject.id);
  const a = accent(meta.accent);
  const Icon = meta.icon;
  const isPreview = subject.status === 'preview';
  // Subjects without real questions yet are shown, but not linked.
  const Card = isPreview ? 'div' : Link;
  const cardProps = isPreview ? { 'aria-disabled': true } : { to: `/subject/${subject.id}` };
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.soft, delay }} whileHover={{ y: -3 }}
    >
      <Card {...cardProps}
        className="group relative block rounded-2xl glass hairline p-6 overflow-hidden h-full transition-colors hover:bg-white/[0.04]">
        <div className={`absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gradient-to-br ${a.grad} blur-2xl opacity-80 group-hover:opacity-100 transition-opacity`} />
        <div className="relative">
          <div className="flex items-center justify-between">
            <div className={`w-12 h-12 rounded-xl ${a.soft} ring-1 ${a.ring} flex items-center justify-center ${a.text}`}>
              <Icon className="w-6 h-6" strokeWidth={1.8} />
            </div>
            {isPreview && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded-md bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20">
                <Lock className="w-3 h-3" /> Coming soon
              </span>
            )}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <div className="font-serif-q text-[22px] text-slate-100 tracking-tight">{subject.name}</div>
            {!isPreview && <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-slate-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />}
          </div>
          <p className="text-[13px] text-slate-400 mt-1.5 leading-relaxed">{subject.blurb}</p>
          <div className="mt-4 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-slate-500">
            <span className={`w-1.5 h-1.5 rounded-full ${isPreview ? 'bg-amber-400' : a.dot}`} />
            {isPreview ? 'Coming soon' : 'Live · ready to practice'}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

function HowItWorksStrip() {
  const items = [
    { icon: Target,    t: 'Practice',   d: 'Solve real JEE Advanced problems from the last 25+ years.' },
    { icon: Bookmark,  t: 'Track',      d: 'Bookmark tough questions and come back to them anytime.' },
  ];
  return (
    <section className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 rounded-2xl overflow-hidden">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div key={it.t} className="bg-[#0b0f1a] p-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 ring-1 ring-indigo-400/20 flex items-center justify-center text-indigo-300">
                <Icon className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="font-serif-q text-[22px] text-slate-100">{it.t}</div>
            </div>
            <div className="text-[13.5px] text-slate-400 mt-3 leading-relaxed">{it.d}</div>
          </div>
        );
      })}
    </section>
  );
}

function OtherExamsStrip() {
  return (
    <section className="mt-10 rounded-2xl glass hairline p-6">
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Preparing for other exams?</div>
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        {SOLVEIT_EXAMS.map((e) => (
          <a key={e.href} href={e.href} target="_blank" rel="noopener"
            className="inline-flex items-center gap-1 font-serif-q text-[18px] text-slate-100 hover:text-indigo-300 transition">
            {e.name} PYQs <ArrowUpRight className="w-4 h-4 text-slate-500" />
          </a>
        ))}
        <span className="text-[13px] text-slate-500">on SolveIT</span>
      </div>
    </section>
  );
}
